import TomSelect from "../../js/tom-select/tom-select.0c9bda91e62b.js";
import {
  createOptionRenderer,
  optionFromTomselectChangedEvent,
} from "./pet-tomselect-utils.b93c6b1d8b56.js";

const alertIcon = `
<svg aria-hidden="true" class="tqc-search-error pv-icon-20 pv-text-critical" style="flex-shrink: 0; margin-bottom: 4px">
  <use xlink:href="#alert-circle"></use>
</svg>
`;

class PetSearchBarValidator extends HTMLElement {
  ERROR_BACKGROUND_SHADE_CLASSNAME = "pv-surface-accent";

  constructor() {
    super();
    this.selectedService = null;
    this.selectedLocation = null;
    this.selectedInsurance = null;
  }

  connectedCallback() {
    this.searchForm = document.querySelector("#rates-form");

    const serviceSelector = document.getElementById("service_code");
    serviceSelector.addEventListener("change", this.handleServiceChanged);
    this.serviceSelector = serviceSelector.tomselect;

    const locationSelector = document.getElementById("care-location");
    locationSelector.addEventListener("change", this.handleLocationChanged);
    this.locationSelector = locationSelector.tomselect;

    const insuranceSelector = document.getElementById("pet-insurance");
    insuranceSelector.addEventListener("change", this.handleInsuranceChanged);
    this.insuranceSelector = insuranceSelector.tomselect;

    this.fieldContainers = {
      service: serviceSelector.parentElement.parentElement,
      location: locationSelector.parentElement.parentElement.parentElement,
      insurance: insuranceSelector.parentElement.parentElement.parentElement,
    };
  }

  clearError = () => {
    for (let element of Object.values(this.fieldContainers)) {
      element.classList.remove(this.ERROR_BACKGROUND_SHADE_CLASSNAME);
    }
    document.querySelectorAll(".tqc-search-error").forEach((el) => {
      el.remove();
    });
  };

  showFieldErrorState = (fieldName) => {
    const field = this.fieldContainers[fieldName];
    if (!field) return;
    field.insertAdjacentHTML("beforeend", alertIcon);
    field.classList.add(this.ERROR_BACKGROUND_SHADE_CLASSNAME);
  };

  addError = (message, fieldName) => {
    const desktopError =
      "<div class='tqc-search-error pv-hide-on-small pv-text-critical pv-text-body-xs' style='margin-top: 8px'>" +
      message +
      "</div>";
    const mobileError =
      "<div class='tqc-search-error pv-hide-on-large search-bar__group pv-text-critical pv-text-body-xs'>" +
      message +
      "</div>";
    this.searchForm.insertAdjacentHTML("afterend", desktopError);
    this.fieldContainers[fieldName].insertAdjacentHTML("afterend", mobileError);
  };

  resetLocations = () => {
    this.locationSelector.clearCache();
    for (const [_locationId, location] of Object.entries(
      this.locationSelector.options,
    )) {
      location.disabled = false;
    }
    this.locationSelector.refreshOptions(false);
  };

  disableInvalidLocations = (validLocations) => {
    this.locationSelector.clearCache();
    for (const [locationId, location] of Object.entries(
      this.locationSelector.options,
    )) {
      if (validLocations.has(parseInt(locationId))) {
        location.disabled = false;
      } else {
        location.disabled = true;
      }
    }
    this.locationSelector.refreshOptions(false);
  };

  handleLocationChanged = (e) => {
    this.clearError();
    this.selectedLocation = optionFromTomselectChangedEvent(e);
    this.validate();
  };

  handleServiceChanged = (e) => {
    this.clearError();
    this.selectedService = optionFromTomselectChangedEvent(e);
    this.updateAllowedLocations();
    this.validate();
  };

  handleInsuranceChanged = (e) => {
    this.clearError();
    this.selectedInsurance = optionFromTomselectChangedEvent(e);
    this.updateAllowedLocations();
    const isValid = this.validate();
    if (isValid) {
      document.dispatchEvent(
        new CustomEvent("tqc:pet-insurance-changed", {
          detail: { networkConfig: this.selectedInsurance },
          bubbles: true,
        }),
      );
    }
  };

  validateUnsupportedSelections = () => {
    const isUnsupportedNetworkSelected =
      this.selectedInsurance?.is_unsupported_for_ssp;
    if (isUnsupportedNetworkSelected) {
      const isSspSelected = this.selectedService?.type == "package";
      if (isSspSelected) {
        return false;
      }
    }

    return true;
  };

  updateAllowedLocations = () => {
    const providerIdsOfferingService = this.selectedService?.provider_ids ?? [];
    const providerIdsAcceptingInsurance =
      this.selectedInsurance?.provider_ids ?? [];

    // If no service or insurance are selected, show all
    if (!this.selectedService && !this.selectedInsurance) {
      this.resetLocations();
      return;
    }

    let allowedProviders;
    if (!this.selectedService) {
      // No service selected, show providers allowed by network selection
      allowedProviders = new Set(providerIdsAcceptingInsurance);
    } else if (!this.selectedInsurance) {
      // No network selected, show providers allowed by service selection
      allowedProviders = new Set(providerIdsOfferingService);
    } else {
      // Both are selected, allow the intersection of network/service
      allowedProviders = new Set(
        providerIdsOfferingService.filter((id) =>
          providerIdsAcceptingInsurance.includes(id),
        ),
      );
    }
    this.disableInvalidLocations(allowedProviders);
  };

  validate = () => {
    const validUnsupportedSelection = this.validateUnsupportedSelections();
    if (!validUnsupportedSelection) {
      this.addError(
        "Service packages are not supported for this plan. Please select a procedure or proceed without insurance to view cash prices.",
        "service",
      );
      this.showFieldErrorState("service");
      return;
    }

    const providerIdsOfferingService = this.selectedService?.provider_ids ?? [];
    const providerIdsAcceptingInsurance =
      this.selectedInsurance?.provider_ids ?? [];

    const selectedProviderId = parseInt(this.locationSelector.getValue());
    if (isNaN(selectedProviderId)) {
      return;
    }

    const validBasedOnService =
      !this.selectedService ||
      providerIdsOfferingService.includes(selectedProviderId);
    const selectedInsuranceValue = this.selectedInsurance?.value
      ? JSON.parse(decodeURIComponent(this.selectedInsurance.value))
      : "";
    const isCashPay = selectedInsuranceValue === "";
    const validBasedOnInsurance =
      isCashPay ||
      !this.selectedInsurance ||
      providerIdsAcceptingInsurance.includes(selectedProviderId);
    if (validBasedOnService && validBasedOnInsurance) {
      return true;
    }

    if (!validBasedOnInsurance) {
      this.addError(
        "This location does not support your selected insurance. Please choose another location or proceed without insurance.",
        "location",
      );
    }

    if (!validBasedOnService) {
      this.addError(
        "This location does not support your selected service. Please select a different location to continue.",
        "location",
      );
    }

    this.showFieldErrorState("location");
    return false;
  };
}

window.customElements.define("pet-search-bar-validator", PetSearchBarValidator);
